import React, { useState } from "react";
import { PrimaryButton, GhostButton, Card } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { CaretDown, CaretRight, Warning, ArrowSquareOut } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const Stat = ({ label, value, cls, testid }) => (
  <div className="border border-white/10 p-2 text-center">
    <div className={`font-heading text-2xl ${cls}`} data-testid={testid}>{value ?? 0}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
  </div>
);

const SummaryRow = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-1.5 border-b border-white/5 text-[11px]">
    <span className="text-zinc-500 uppercase tracking-widest text-[10px]">{label}</span>
    <span className="text-zinc-200 text-right">{children}</span>
  </div>
);

export default function TedRunModal({ open, result, onClose }) {
  const [showQuery, setShowQuery] = useState(false);
  const nav = useNavigate();
  if (!open || !result) return null;

  const r = result;
  const fmt = (t) => (t ? new Date(t).toLocaleString() : "—");

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" data-testid="ted-run-modal">
      <Card className="max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Discovery · TED Connector</div>
            <h2 className="font-heading text-xl tracking-tighter">TED Manual Run · Result</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="ted-run-close">×</button>
        </div>

        {/* Run summary */}
        <div className="mb-4" data-testid="ted-run-summary">
          <SummaryRow label="Search Rule">{r.rule_name || "—"}</SummaryRow>
          <SummaryRow label="Source">TED</SummaryRow>
          <SummaryRow label="Run Type">{r.run_type || "Manual"}</SummaryRow>
          <SummaryRow label="Status"><StatusPill value={r.run_status} /></SummaryRow>
          <SummaryRow label="Started">{fmt(r.started_at)}</SummaryRow>
          <SummaryRow label="Completed">{fmt(r.completed_at)}</SummaryRow>
          <SummaryRow label="Run By">{r.run_by_email || "—"}</SummaryRow>
        </div>

        {/* Result counts */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4" data-testid="ted-run-counts">
          <Stat label="Found" value={r.total_results_found} cls="text-zinc-200" testid="ted-stat-found" />
          <Stat label="Created" value={r.candidates_created} cls="text-[#00E676]" testid="ted-stat-created" />
          <Stat label="Possible Dups" value={r.possible_duplicates_created} cls="text-[#FFB300]" testid="ted-stat-possible" />
          <Stat label="Exact Skipped" value={r.exact_duplicates_skipped} cls="text-zinc-400" testid="ted-stat-skipped" />
          <Stat label="Errors" value={r.errors_count} cls={r.errors_count ? "text-[#FF3B30]" : "text-zinc-500"} testid="ted-stat-errors" />
        </div>

        {/* V2 breakdown: created candidates by deterministic confidence + manual-download */}
        {(r.confidence_breakdown || r.manual_download_count != null) && (
          <div className="mb-4" data-testid="ted-run-v2-breakdown">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Created by Confidence · Manual Download</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <Stat label="High" value={r.confidence_breakdown?.High} cls="text-[#00E676]" testid="ted-stat-conf-high" />
              <Stat label="Medium" value={r.confidence_breakdown?.Medium} cls="text-[#00E5FF]" testid="ted-stat-conf-medium" />
              <Stat label="Low" value={r.confidence_breakdown?.Low} cls="text-[#FFB300]" testid="ted-stat-conf-low" />
              <Stat label="Noise" value={r.confidence_breakdown?.Noise} cls="text-[#FF3B30]" testid="ted-stat-conf-noise" />
              <Stat label="Manual DL" value={r.manual_download_count} cls="text-[#FFB300]" testid="ted-stat-md" />
              <Stat label="No Deadline" value={r.missing_deadline_count} cls="text-zinc-400" testid="ted-stat-missing-deadline" />
            </div>
          </div>
        )}

        {/* Warnings */}
        {Array.isArray(r.warnings) && r.warnings.length > 0 && (
          <div className="mb-4 border border-[#FFB300]/30 bg-[#FFB300]/[0.06] p-3" data-testid="ted-run-warnings">
            <div className="text-[10px] uppercase tracking-widest text-[#FFB300] mb-1.5 flex items-center gap-1">
              <Warning size={12} /> Warnings
            </div>
            <ul className="space-y-1 text-[11px] text-zinc-300">
              {r.warnings.map((w, i) => (
                <li key={i} className="border-l-2 border-[#FFB300] pl-2" data-testid={`ted-warning-${i}`}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Collapsible query */}
        <div className="mb-4 border border-white/10">
          <button
            onClick={() => setShowQuery((s) => !s)}
            className="w-full flex items-center justify-between px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
            data-testid="ted-query-toggle"
          >
            <span className="flex items-center gap-1">{showQuery ? <CaretDown size={11} /> : <CaretRight size={11} />} Query used</span>
            <span className="text-zinc-600">TED expert syntax</span>
          </button>
          {showQuery && (
            <pre className="px-3 py-2 text-[11px] font-mono-data text-zinc-300 whitespace-pre-wrap break-words border-t border-white/10" data-testid="ted-query-text">
              {r.query_used || "—"}
            </pre>
          )}
        </div>

        {/* Next actions */}
        <div className="flex flex-wrap justify-end gap-2">
          <GhostButton onClick={() => nav("/candidates")} data-testid="ted-view-queue-btn">
            <ArrowSquareOut size={11} className="inline mr-1" /> Candidate Review Queue
          </GhostButton>
          <GhostButton onClick={() => nav("/search-runs")} data-testid="ted-view-run-btn">
            <ArrowSquareOut size={11} className="inline mr-1" /> Search Run Details
          </GhostButton>
          <PrimaryButton onClick={onClose} data-testid="ted-run-done-btn">Done</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}
